import { z } from "zod";
import {
  CARDS_STORE,
  PRACTICE_STORE,
  META_STORE,
  learningTransaction,
  openLearningDatabase,
  learningGeneration,
  replaceGeneration,
} from "./database.ts";
import {
  getPracticeStore,
  createMemoryPracticeStore,
  type PracticeStore,
  practiceRecordSchema,
} from "../practice/store.ts";
import { createSm2Card, type Sm2Card } from "./sm2.ts";
import type { AnalyticsEvent } from "../analytics/events.ts";

export type MasteryRecord = Sm2Card & {
  id: string;
  courseId: string;
  openingId: string;
  positionKey: string;
  fen: string;
  recommendedSan: string;
  lastRetrievedAt?: number;
  independentReps?: number;
  revision?: number;
  lastOutcomeId?: string;
  contentVersion?: number;
};

export const masteryRecordSchema = z.object({
  id: z.string().min(1),
  courseId: z.string().min(1),
  openingId: z.string().min(1),
  positionKey: z.string().min(8),
  fen: z.string().min(8),
  recommendedSan: z.string().min(1),
  lastRetrievedAt: z.number().finite().optional(),
  independentReps: z.number().int().nonnegative().optional(),
  revision: z.number().int().nonnegative().optional(),
  lastOutcomeId: z.string().optional(),
  contentVersion: z.number().int().positive().optional(),
  n: z.number(),
  ef: z.number(),
  interval: z.number(),
  due: z.number(),
  box: z.number(),
  lastGrade: z.number(),
  reps: z.number(),
  lapses: z.number(),
});

export const reviewDumpSchema = z.object({
  version: z.union([z.literal(1), z.literal(2)]),
  exportedAt: z.string().min(1),
  cards: z.array(masteryRecordSchema),
  practice: z.array(practiceRecordSchema).max(20_000).optional(),
  events: z
    .array(
      z.object({
        name: z.string(),
        at: z.number(),
        props: z.record(z.unknown()),
      })
    )
    .optional(),
});

export type ReviewDump = z.infer<typeof reviewDumpSchema>;

export type ReviewStore = {
  practiceStore?: PracticeStore;
  generation?(): Promise<number>;
  get(id: string): Promise<MasteryRecord | null>;
  put(card: MasteryRecord): Promise<void>;
  putIfCurrent?(card: MasteryRecord, revision: number, generation?: number): Promise<boolean>;
  /** Forget one position (a take-back of a position first saved this session). */
  remove(id: string): Promise<void>;
  /** Called after clear() or importDump() replaces what is stored. */
  onReplace?(listener: () => void): () => void;
  list(): Promise<MasteryRecord[]>;
  due(now?: number): Promise<MasteryRecord[]>;
  importDump(dump: ReviewDump): Promise<void>;
  exportDump(events?: AnalyticsEvent[]): Promise<ReviewDump>;
  clear(): Promise<void>;
};

const STORE = CARDS_STORE;

/** Listeners told when a store's contents are replaced wholesale. */
function replaceListeners() {
  const listeners = new Set<() => void>();
  return {
    onReplace(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    notify() {
      for (const listener of listeners) listener();
    },
  };
}

export function createMemoryReviewStore(
  seed: MasteryRecord[] = [],
  practice = createMemoryPracticeStore()
): ReviewStore {
  const cards = new Map<string, MasteryRecord>(seed.map((card) => [card.id, { ...card }]));
  const replaced = replaceListeners();
  let generation = 0;

  return {
    async generation() {
      return generation;
    },
    practiceStore: practice,
    async get(id) {
      const card = cards.get(id);
      return card ? { ...card } : null;
    },
    async put(card) {
      cards.set(card.id, { ...card });
    },
    async putIfCurrent(card, revision, expectedGeneration) {
      if (expectedGeneration !== undefined && expectedGeneration !== generation) return false;
      if ((cards.get(card.id)?.revision || 0) !== revision) return false;
      cards.set(card.id, { ...card, revision: revision + 1 });
      return true;
    },
    async remove(id) {
      cards.delete(id);
    },
    async list() {
      return [...cards.values()].map((card) => ({ ...card }));
    },
    async due(now = Date.now()) {
      return [...cards.values()].filter((card) => card.due <= now).map((card) => ({ ...card }));
    },
    async importDump(dump) {
      const parsed = reviewDumpSchema.parse(dump);
      await practice.replace(parsed.practice || []);
      cards.clear();
      for (const card of parsed.cards) cards.set(card.id, { ...card });
      generation += 1;
      replaced.notify();
    },
    async exportDump(events = []) {
      return {
        version: 2,
        exportedAt: new Date().toISOString(),
        cards: [...cards.values()].map((card) => ({ ...card })),
        practice: await practice.list(),
        events: [...events],
      };
    },
    async clear() {
      cards.clear();
      await practice.replace([]);
      generation += 1;
      replaced.notify();
    },
    onReplace: replaced.onReplace,
  };
}

export function createIndexedDbReviewStore(): ReviewStore {
  const tx = <T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>) =>
    learningTransaction(STORE, mode, fn);
  const replaced = replaceListeners();

  return {
    generation: learningGeneration,
    async get(id) {
      const row = await tx("readonly", (store) => store.get(id));
      return (row as MasteryRecord) || null;
    },
    async put(card) {
      await tx("readwrite", (store) => store.put(card));
    },
    async putIfCurrent(card, revision, generation) {
      const db = await openLearningDatabase();
      return new Promise<boolean>((resolve, reject) => {
        const transaction = db.transaction([STORE, META_STORE], "readwrite");
        const store = transaction.objectStore(STORE);
        let saved = false;
        const epoch = transaction.objectStore(META_STORE).get("generation");
        epoch.onsuccess = () => {
          if (generation !== undefined && (epoch.result?.value || 0) !== generation) return;
          const request = store.get(card.id);
          request.onsuccess = () => {
            if ((request.result?.revision || 0) !== revision) return;
            store.put({ ...card, revision: revision + 1 });
            saved = true;
          };
        };
        transaction.oncomplete = () => {
          db.close();
          resolve(saved);
        };
        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
        transaction.onabort = () => {
          db.close();
          reject(transaction.error);
        };
      });
    },
    async remove(id) {
      await tx("readwrite", (store) => store.delete(id));
    },
    async list() {
      const rows = await tx("readonly", (store) => store.getAll());
      return (rows as MasteryRecord[]) || [];
    },
    async due(now = Date.now()) {
      const rows = await this.list();
      return rows.filter((card) => card.due <= now);
    },
    async importDump(dump) {
      const parsed = reviewDumpSchema.parse(dump);
      const db = await openLearningDatabase();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction([STORE, PRACTICE_STORE, META_STORE], "readwrite");
        const store = tx.objectStore(STORE);
        replaceGeneration(tx);
        const practice = tx.objectStore(PRACTICE_STORE);
        practice.clear();
        for (const record of parsed.practice || []) practice.put(record);
        store.clear();
        for (const card of parsed.cards) store.put(card);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error);
        };
      });
      replaced.notify();
    },
    async exportDump(events = []) {
      return {
        version: 2,
        exportedAt: new Date().toISOString(),
        cards: await this.list(),
        practice: await getPracticeStore().list(),
        events: [...events],
      };
    },
    async clear() {
      await learningTransaction([STORE, PRACTICE_STORE, META_STORE], "readwrite", (store) => {
        replaceGeneration(store.transaction);
        store.transaction.objectStore(PRACTICE_STORE).clear();
        return store.clear();
      });
      replaced.notify();
    },
    onReplace: replaced.onReplace,
  };
}

let defaultStore: ReviewStore | null = null;

export function getReviewStore(): ReviewStore {
  if (!defaultStore) {
    defaultStore =
      typeof indexedDB === "undefined" ? createMemoryReviewStore() : createIndexedDbReviewStore();
  }
  return defaultStore;
}

export function setReviewStoreForTests(store: ReviewStore | null) {
  defaultStore = store;
}

export function newMasteryRecord(args: {
  courseId: string;
  openingId: string;
  positionKey: string;
  fen: string;
  recommendedSan: string;
  now?: number;
}): MasteryRecord {
  return {
    id: `${args.courseId}:${args.positionKey}`,
    courseId: args.courseId,
    openingId: args.openingId,
    positionKey: args.positionKey,
    fen: args.fen,
    recommendedSan: args.recommendedSan,
    ...createSm2Card(args.now),
  };
}
